import { useState } from 'react'
import { InteractiveRenderer } from '@aurora/interactives'
import type { InteractiveResult } from '@aurora/core'
import { useAuroraStore } from '../../store/auroraStore'
import { skillLabels } from '../../store/selectors'
import { difficultyLabel, kindLabel, readSpec } from '../../lib/specs'
import { useResolvedTheme } from '../../hooks/useTheme'
import { KindMark } from './KindMark'

/**
 * What came back, before it is kept.
 *
 * The preview is the real thing, playable — a still picture of an activity
 * tells a parent nothing about whether it works, and the one question worth
 * answering here is "would this hold her for ten minutes?". Playing it through
 * once is the only way to know.
 *
 * It goes through the same `readSpec` guard Kid Mode uses, so a malformed spec
 * is caught here, in front of the adult, rather than in front of the child.
 */
export function SpecPreview({ artifact }: { artifact: any }) {
  const labels = useAuroraStore(skillLabels)
  const theme = useResolvedTheme()
  const [attempt, setAttempt] = useState(0)
  const [played, setPlayed] = useState<InteractiveResult | null>(null)

  const read = readSpec(artifact)

  if (!read.ok) {
    return (
      <div className="preview">
        <div className="preview-broken">
          <p className="preview-broken-title">This one came back malformed</p>
          <p className="preview-broken-body">
            Nothing was saved and nothing reached Kid Mode. Asking again, or describing it a
            little differently, usually gets a clean one.
          </p>
          <ul className="preview-broken-list">
            {read.errors.slice(0, 4).map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  const spec = read.spec
  const skills = spec.targetSkillIds ?? []

  return (
    <div className="preview">
      <header className="preview-head">
        <span className="preview-mark" aria-hidden="true">
          <KindMark kind={spec.kind} />
        </span>
        <div className="preview-head-text">
          <p className="preview-title">{spec.title}</p>
          <p className="preview-meta">
            {kindLabel(spec.kind)} · {difficultyLabel(spec.difficulty)}
            {spec.theme ? ` · ${spec.theme}` : ''}
          </p>
        </div>
      </header>

      {skills.length > 0 && (
        <ul className="preview-skills">
          {skills.map((id) => (
            <li key={id} className="preview-skill">
              {labels[id] ?? id}
            </li>
          ))}
        </ul>
      )}

      <div className="preview-stage">
        <InteractiveRenderer
          key={attempt}
          spec={spec}
          theme={theme}
          onResult={(result: InteractiveResult) => setPlayed(result)}
        />
      </div>

      <footer className="preview-foot">
        <p className="preview-hint">
          {played
            ? played.completed
              ? 'Plays through cleanly.'
              : 'Ended early — worth another look before you keep it.'
            : 'Try it yourself. This is exactly what she will see.'}
        </p>
        {played && (
          <button className="btn btn-quiet btn-sm" onClick={() => {
            setPlayed(null)
            setAttempt((n) => n + 1)
          }}>
            Reset preview
          </button>
        )}
      </footer>
    </div>
  )
}
